import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  EmptyState,
  Tabs,
  TabItem,
  buttonClasses,
  failedEmptyState,
  notFoundEmptyState,
  useIsMobile,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import ClientDetailSkeleton from "../components/ClientDetailSkeleton";
import ApprovePreconsultationModal from "../components/ApprovePreconsultationModal";
import PreconsultationBanner, {
  sendIcon,
} from "../components/PreconsultationBanner";
import PreconsultationReviewModal from "../components/PreconsultationReviewModal";
import RejectPreconsultationModal from "../components/RejectPreconsultationModal";
import PreconsultationSharedModal from "../components/PreconsultationSharedModal";
import ClientDetailsTab from "../components/ClientDetailsTab";
import ClientPaymentsTab from "../components/ClientPaymentsTab";
import ClientPlansTab from "../components/ClientPlansTab";
// Appointments are their own feature — see its barrel. This screen is one of
// its callers, the same way it is a caller of meal plans and payments.
import {
  CancelAppointmentModal,
  ClientAppointmentsTab,
  CompleteAppointmentModal,
  NextAppointmentBanner,
  MEETING_NOTICE_MS,
  appointmentShortDate,
  cancelledWithoutAppNote,
  meetingAccountNotice,
  nextToday,
  useClientAppointments,
  useMeetingApps,
  type AppointmentAnswer,
} from "../../appointments";
import { toClientDetail } from "../clientMapping";
import { submissionKeysOf } from "../clientNews";
import { markClientOpened, markPreconsultationSeen } from "../openedClients";
import { useClient } from "../hooks/useClient";
import { useWaitingSubmissions } from "../hooks/useWaitingSubmissions";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { ApiError } from "../../../lib/http";
import {
  approvePreconsultationForm,
  createPreconsultationForm,
  rejectPreconsultationForm,
  type PreconsultationFormLink,
} from "../../preconsultation/services/preconsultationFormsService";
// A deep import rather than the billing barrel: that barrel pulls in the
// checkout and result screens, and this page only needs to know whether the
// workspace is allowed to change anything.
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import { ClientDetailTabId } from "../types/clients.types";
import { ROUTES, clientEditPath } from "../../../routes/paths";
import { decodeRouteId } from "../../../routes/routeIds";

// Plans first, and the tab the screen opens on: what a client is opened to
// look at is the plan they are on, with the payments behind it and the record
// itself — which changes rarely and is edited from its own screen — last.
const TABS: TabItem<ClientDetailTabId>[] = [
  { id: "plans", label: "Plans" },
  { id: "payments", label: "Payments" },
  { id: "details", label: "Details" },
  { id: "appointment", label: "Appointment" },
];

/**
 * Which tab the URL is asking for.
 *
 * Anything unknown — a missing `?tab=`, or a name from a link written before
 * this screen had these four — falls to the first tab rather than to nothing.
 */
const tabFromParam = (value: string | null): ClientDetailTabId =>
  TABS.some((tab) => tab.id === value)
    ? (value as ClientDetailTabId)
    : TABS[0].id;

/**
 * The first word of a client's name, for the strip that asks for their health
 * details by name. The record's own `firstName` is not reachable here — the
 * screen holds the mapped `ClientDetail`, which carries the two halves already
 * joined — and the join is a single space, so the split is exact. A one-word
 * name is its own first name; "Unnamed client" reads as "Unnamed", which is as
 * close to right as a client with no name on record allows.
 */
const firstNameOf = (name: string): string => name.split(" ")[0];

/**
 * How far into ruling on a submitted pre-consultation the screen is.
 *
 * One value rather than three flags, because they are steps and not states that
 * can hold at once: opening the confirm closes the form behind it, and two
 * dialogs stacked would leave a reader unsure which one their Escape answers.
 */
type ReviewStep = "none" | "form" | "approve" | "reject";

/**
 * Whether the link dialog is up, and which errand it is up for: a link just
 * raised, or one already out being handed over again.
 *
 * Held rather than worked out from the record, because the record moves under
 * it. Raising a link re-reads the client, and the form that read brings back
 * is `PENDING` — so a dialog reading its own words off the record would open
 * saying a link has just been sent and change its mind to "here is the one
 * already out" the moment the re-read landed.
 */
type LinkView = "none" | "fresh" | "resend";

const ClientDetailPage: React.FC = () => {
  // The param is the enciphered token `clientDetailPath` wrote, not the id
  // itself. A token that does not decode — mistyped, truncated in a chat, or
  // made before `REACT_APP_ROUTE_ID_SECRET` last changed — comes back as "",
  // which `useClient` already reports as a missing client.
  const { clientId: token = "" } = useParams();
  const clientId = decodeRouteId(token);

  const navigate = useNavigate();

  // In the URL rather than in state, so the tab survives a refresh and can be
  // linked to — which is how the plan builder returns someone to the Plans tab
  // they created a plan from.
  const [searchParams, setSearchParams] = useSearchParams();

  const isMobile = useIsMobile();
  // All four at every width. The Plans tab used to be dropped on a phone,
  // because the one thing it offered was a way into the plan builder and the
  // app does not carry that screen at this width. It offers something else
  // now: the three lists, and the one act a dietitian performs on a finished
  // plan from a phone — sending it to the client. See `ClientPlansTab`.
  const activeTab = tabFromParam(searchParams.get("tab"));

  // Replaced rather than pushed: Back should leave the client, not walk back
  // through every tab that was looked at along the way.
  const setActiveTab = (tab: ClientDetailTabId) =>
    setSearchParams({ tab }, { replace: true });

  // The id in the route is the `public_id` the list rows carry, so the screen
  // asks for that one client rather than reading it out of the list — which is
  // also what makes a bookmarked detail page, or a refresh, load on its own.
  // `live` because of what this screen is usually open for: a form the client
  // is filling in right now. Left to itself the strip would only appear on a
  // reload, which is the one thing somebody watching for it will not think to
  // do. See `UseClientOptions`.
  const { client: record, status, reload } = useClient(clientId, { live: true });

  // Read before the returns below, since a hook cannot sit behind one.
  const phase = useLoadPhase(status === "loading");

  // Read once here and handed to the tabs rather than asked for in each of
  // them: the billing call is not cached between mounts, so a hook per tab
  // would fetch the same answer again every time the tabs were switched — and
  // the three have to agree about whether the door is shut anyway. What each
  // tab does with it is its own: this screen only reads and writes one
  // client's record, and none of that is offered to a workspace with no plan
  // behind it. See `useSubscriptionGate`.
  const gate = useSubscriptionGate();

  // Read here rather than inside the Appointment tab, because the strip it
  // feeds stands above the tabs: somebody who opened this client to look at
  // their plan still has to be told there is a call with them in two hours,
  // and a strip that only appeared once they had gone looking for it would be
  // telling them something they had just found out.
  //
  // The tab below calls the same hook. That is not a second request — both
  // read one cache entry keyed by this client, so whichever mounts first is
  // the one that fetches, and a write from the tab moves this strip with it.
  const { appointments, cancel: cancelAppointment, complete } =
    useClientAppointments(clientId);

  // The strip's own dialog. It is raised here rather than inside the
  // Appointment tab because the strip stands above the tabs: the appointment
  // can be marked from the Plans tab, where that tab is not mounted at all.
  // Both write through one cache entry, so a row marked from either place is
  // marked in the other.
  const [marking, setMarking] = useState<AppointmentAnswer | null>(null);
  const [answering, setAnswering] = useState(false);

  // Raising a pre-consultation link, and the dialog that hands the raised one
  // over. `sendingForm` is the call in flight; `sharedLink` is the link this
  // session is holding, and `linkShown` is whether the dialog is up.
  //
  // Two pieces rather than one, because closing the dialog is not losing the
  // link: a backend that does not carry `share_url` on the client record
  // leaves the one raised here as the only copy of the address, and a Resend
  // after the dialog was dismissed would otherwise have to mint a second link
  // to show the practitioner the first one. See `LinkView`.
  const [sendingForm, setSendingForm] = useState(false);
  const [sharedLink, setSharedLink] = useState<PreconsultationFormLink | null>(
    null
  );
  const [linkView, setLinkView] = useState<LinkView>("none");

  // Reading a submitted form and ruling on it — the other half of that strip.
  const [reviewStep, setReviewStep] = useState<ReviewStep>("none");
  // A decision already on its way. Holds both dialogs' buttons, so a slow
  // network cannot be answered twice.
  const [deciding, setDeciding] = useState(false);

  const toast = useToast();
  const getToken = useTokenGetter();
  // Only for the line a cancellation adds about an online appointment whose
  // meeting app has since been disconnected. One cached answer for the
  // workspace, shared with the Appointment tab this screen already draws.
  const meetingApps = useMeetingApps();

  // Memoised because the Plans tab filters `mealPlans` in a memo of its own,
  // and a new array on every render would rebuild that list each time.
  const client = useMemo(
    () => (record ? toClientDetail(record) : undefined),
    [record]
  );

  /**
   * Every key the submission waiting on this client answers to, out of both
   * places that know about one.
   *
   * Two sources, because they do not always agree. The client's own record
   * carries the forms raised for them — but a record can come back naming only
   * the link currently out, with the submission that came before it nowhere on
   * it, which is exactly the state this screen was found in: the strip said a
   * link was still waiting while the list behind it flagged answers that had
   * arrived. Read from the record alone there was nothing to mark, and the tag
   * could not be cleared by any act at all — there was not even a form on
   * screen to press. So whatever the forms endpoint attributes to this client
   * is marked alongside it. See `useWaitingSubmissions`.
   *
   * Joined into a string for the dependency array below, so the quiet refresh
   * above — which hands back a fresh record every minute — does not re-run the
   * effect on every tick for a submission that has not changed.
   */
  const waiting = useWaitingSubmissions();
  const waitingOn = useMemo(() => {
    const keys = record ? submissionKeysOf(record) : [];
    const reported = waiting.get(clientId)?.key;
    if (reported && !keys.includes(reported)) keys.push(reported);
    return keys.join("|");
  }, [record, waiting, clientId]);

  /**
   * The client marked as opened, and the answers waiting on them marked as
   * read along with it.
   *
   * Opening the client is the act. This has been drawn tighter twice — first
   * to the Details tab, then to pressing View full form or ruling on the
   * submission — on the reasoning that checking whether a form had landed
   * should not be what retires the news about it. That reasoning was sound and
   * the conclusion was wrong: a practitioner who has opened a client has been
   * taken to everything this screen has to say about them, and a flag that
   * survives the visit is a flag that has to be dismissed some other way. There
   * is no other way here, and for a client whose record does not carry the
   * submission there is not even a form to open.
   *
   * So both go on arrival. The rest of the news — the rail's count, the row's
   * tag — follows from the same store, and the list behind this screen is
   * clean by the time they walk back to it.
   */
  useEffect(() => {
    if (!clientId || status !== "ready") return;
    markClientOpened(clientId);
    if (waitingOn) markPreconsultationSeen(waitingOn.split("|"));
  }, [clientId, status, waitingOn]);

  // The Details tab drawn as blocks while the record is fetched, so the client
  // lands into a page that is already the shape it will keep — but only once
  // the fetch has visibly stalled. A client that arrives quickly opens on
  // itself, with no screenful of blocks in between.
  if (status === "loading") return <ClientDetailSkeleton phase={phase} />;

  // A failed load is not a missing client, and saying so would send someone
  // looking for a record that is sitting there behind an error. Only the
  // failure is worth retrying — a 404 will keep being a 404.
  if (status !== "ready" || !client) {
    const failed = status === "failed";

    return (
      <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top">
        <PageHeader
          title={failed ? "Could not load this client" : "Client not found"}
          breadcrumbs={[
            { label: "My clients", to: ROUTES.myClients },
            { label: failed ? "Client unavailable" : "Client not found" },
          ]}
        />
        {/* In a card of its own, so the state sits on the page the way a
            client's details would rather than floating under the header. */}
        <div className="mt-5 flex-1 min-h-0 flex items-center justify-center card-surface border border-line bg-white">
          <EmptyState
            {...(failed
              ? failedEmptyState(
                  "We could not reach the server",
                  <Button size="sm" variant="secondary" onClick={reload}>
                    Try again
                  </Button>
                )
              : notFoundEmptyState(
                  "That client no longer exists",
                  "They may have been deleted. Everyone else on your list is unaffected.",
                  // A link and not a button: this goes somewhere, so it should
                  // open in a new tab on a middle click and be announced as
                  // the way out that it is.
                  <Link
                    to={ROUTES.myClients}
                    className={buttonClasses({
                      variant: "secondary",
                      size: "sm",
                    })}
                  >
                    Back to my clients
                  </Link>
                ))}
          />
        </div>
      </div>
    );
  }

  const submitted = client.preconsultation;
  const firstName = firstNameOf(client.name);

  // Worked out after the returns above, so it is only ever asked of a screen
  // that has a client to draw. `undefined` on every day this client is not
  // being seen, which is most of them — see `nextToday`.
  const today = nextToday(appointments);

  /**
   * Says what became of today's appointment, from the strip above the tabs.
   *
   * The same two acts the table's own rows offer, written the same way — see
   * `ClientAppointmentsTab`. The dialog stays up on a failure so the answer
   * can be given again rather than being lost with it.
   *
   * The reason only reaches the cancellation; a session that took place has
   * nothing to explain, and its dialog does not ask.
   */
  const handleAnswerToday = async (reason: string) => {
    if (!today || !marking || answering) return;
    const completing = marking === "completed";
    setAnswering(true);

    try {
      if (completing) await complete(today.id);
      else await cancelAppointment(today.id, reason);

      setMarking(null);
      // The invitation nobody withdrew, where the appointment was online and
      // the app that made its link has since been disconnected. Only a
      // cancellation says it: a session that took place has nothing left in
      // anybody's calendar to tidy up.
      const note = completing
        ? null
        : cancelledWithoutAppNote(today.type, meetingApps);
      toast.success(
        completing ? "Appointment completed" : "Appointment cancelled",
        completing
          ? `The ${appointmentShortDate(today.startsAt)} session with ${firstName} is on their record.`
          : `The ${appointmentShortDate(today.startsAt)} appointment with ${firstName} has been called off.${
              note ? ` ${note}` : ""
            }`,
        note ? MEETING_NOTICE_MS : undefined
      );
    } catch (error) {
      // A meeting made on another account is refused whatever was being done
      // to it, so it is not described as a failure to complete or to cancel —
      // see `meetingAccountNotice`.
      const mismatch = meetingAccountNotice(error);
      if (mismatch) {
        toast.error(mismatch.title, mismatch.message, MEETING_NOTICE_MS);
      } else {
        toast.error(
          completing
            ? "Could not mark this appointment completed"
            : "Could not cancel this appointment",
          error instanceof ApiError
            ? error.message
            : "We could not reach the server. Check your connection and try again."
        );
      }
      console.error("answering an appointment failed:", error);
    } finally {
      setAnswering(false);
    }
  };

  /**
   * Both decisions, which differ only in the call they make and what they say
   * afterwards.
   *
   * The client is re-read rather than patched from the response: approving
   * merges the answers onto the record, and what that record now holds is the
   * backend's answer to give, not something to assemble out of a form. The
   * re-read is also what moves the strip off its submitted state.
   */
  const decide = async (
    run: () => Promise<unknown>,
    heading: string,
    detail: string
  ) => {
    if (deciding || !submitted?.id) return;
    setDeciding(true);

    try {
      await run();
      setReviewStep("none");
      toast.success(heading, detail);
      reload();
    } catch (error) {
      toast.error(
        "Could not save that decision",
        error instanceof ApiError
          ? error.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("ruling on a pre-consultation failed:", error);
    } finally {
      setDeciding(false);
    }
  };

  const handleApprove = () =>
    decide(
      () => approvePreconsultationForm(getToken, submitted!.id),
      "Details approved",
      `${firstName}'s answers are on their profile.`
    );

  const handleReject = (reason: string) =>
    decide(
      () => rejectPreconsultationForm(getToken, submitted!.id, reason),
      "Submission rejected",
      `${firstName} has been told what to fix.`
    );

  /**
   * Raises a link and shows it.
   *
   * Nothing is asked first. The button says what it does, and a dialog asking
   * whether it was meant only put a step between an intention and the one
   * thing it could have meant. A fresh link retires whatever came before it,
   * but nowhere this runs from has a live link to retire: it is offered only
   * to a client who has never been sent one, or whose last one is spent.
   */
  const handleSend = async () => {
    if (sendingForm) return;
    setSendingForm(true);

    try {
      setSharedLink(
        await createPreconsultationForm(getToken, { clientId: client.id })
      );
      setLinkView("fresh");
      // The client is where the fact that a form is out lives, and re-reading
      // is what moves the strip from offering to send one to waiting on it.
      reload();
    } catch (error) {
      toast.error(
        "Could not create the link",
        error instanceof ApiError
          ? error.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("minting a pre-consultation link failed:", error);
    } finally {
      setSendingForm(false);
    }
  };

  /**
   * The link already out, put back in front of the practitioner so they can
   * send it again.
   *
   * The same dialog it was first handed over in, rather than a silent copy to
   * the clipboard: "Resend" means getting it to the client, and the ways of
   * doing that — WhatsApp with the message written and their own number in it,
   * or the address to paste wherever they actually read things — are what that
   * dialog carries. A clipboard write with a toast over it did none of that,
   * and on a browser that refuses the clipboard it did nothing at all.
   *
   * No second link is raised. The one on the record is the one the client is
   * holding, and minting another would kill a form they may be halfway
   * through — see `fresh` on the dialog.
   *
   * Off the record where the backend carries the address, and off the one
   * raised in this session where it does not. With neither there is nothing to
   * hand over, and the only honest thing left is to raise a link.
   */
  const handleResend = () => {
    // The record first, since it is the server's own word on which link is
    // live; the one raised in this session behind it, for a backend that sends
    // the record without an address on it.
    if (submitted?.shareUrl) {
      // Built here rather than fetched: the summary already carries everything
      // the dialog reads, and asking the server to describe a link the screen
      // is holding would be a round trip for nothing.
      setSharedLink({
        id: submitted.id,
        url: submitted.shareUrl,
        expiresAt: submitted.expiresAt,
        status: submitted.status,
        subject: { type: "client", id: client.id, name: client.name },
      });
    } else if (!sharedLink) {
      return handleSend();
    }

    setLinkView("resend");
  };

  // Nothing live, and one sent before: the card has said its piece already, so
  // the offer to send another is a button beside the client's name. On the
  // Details tab alone, like the strip it stands in for.
  const bareSend =
    activeTab === "details" && !submitted && client.preconsultationEverSent;

  // Beside the name from `md` up. On a phone it goes under the name instead,
  // the full width of the screen: it is the longest label in the app's
  // headers, and set beside a person's name on a 360px screen it left the
  // name three letters and an ellipsis.
  const sendButton = bareSend && (
    <Button
      size="md"
      variant="invite"
      className={isMobile ? "mt-4 w-full" : undefined}
      onClick={handleSend}
      loading={sendingForm}
      endIcon={sendIcon}
      {...gate.props("send a pre-consultation form")}
    >
      Send consultation link
    </Button>
  );

  return (
    // Same full-height shell as the list: the page never scrolls, only the
    // tables inside the tabs do.
    <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top max-md:pb-0">
      <PageHeader
        title={client.name}
        breadcrumbs={[
          { label: "My clients", to: ROUTES.myClients },
          { label: client.name },
        ]}
        actions={!isMobile && sendButton}
      />

      {isMobile && sendButton}

      {/* Above the tabs and on every one of them, because it is not about a
          tab — see `NextAppointmentBanner`. Today only, and only while the
          appointment is still ahead, so it clears itself once the call has
          been and gone. */}
      {today && (
        <div className="mt-5 flex-shrink-0">
          <NextAppointmentBanner
            appointment={today}
            gate={gate.props("mark an appointment")}
            onAnswer={setMarking}
          />
        </div>
      )}

      {/* Over the Details tab alone, because that is the tab the answers land
          on: the row of dashes the card is explaining is right underneath it.
          On Plans or Payments it would be a strip about a form standing over a
          table that has nothing to do with one. */}
      {activeTab === "details" && (
        <PreconsultationBanner
          firstName={firstName}
          form={submitted}
          everSent={client.preconsultationEverSent}
          sending={sendingForm}
          onSend={handleSend}
          onResend={handleResend}
          onView={() => setReviewStep("form")}
          gate={gate}
        />
      )}

      <div className="mt-5 flex-shrink-0 border-b border-line">
        <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      <div className="mt-5 flex-1 min-h-0 flex flex-col overflow-y-auto custom-scrollbar-overlay max-md:pb-4">
        {activeTab === "details" && (
          <ClientDetailsTab
            client={client}
            gate={gate}
            onEdit={() => navigate(clientEditPath(client.id))}
          />
        )}
        {activeTab === "plans" && <ClientPlansTab client={client} gate={gate} />}
        {activeTab === "payments" && (
          <ClientPaymentsTab client={client} gate={gate} />
        )}
        {activeTab === "appointment" && (
          <ClientAppointmentsTab
            client={client}
            gate={gate}
            firstName={firstName}
          />
        )}
      </div>

      {/* Only ever one of the three, and only while there is a form to rule on:
          a decision dialog outliving its form would be answering about
          something that has already moved on. */}
      {submitted && reviewStep === "form" && (
        <PreconsultationReviewModal
          clientName={`${client.salutation} ${client.name}`.trim()}
          answers={submitted.answers}
          gate={gate}
          onAccept={() => setReviewStep("approve")}
          onReject={() => setReviewStep("reject")}
          onClose={() => setReviewStep("none")}
        />
      )}

      {submitted && reviewStep === "approve" && (
        <ApprovePreconsultationModal
          firstName={firstName}
          busy={deciding}
          onConfirm={handleApprove}
          onClose={() => setReviewStep("none")}
        />
      )}

      {submitted && reviewStep === "reject" && (
        <RejectPreconsultationModal
          firstName={firstName}
          busy={deciding}
          onConfirm={handleReject}
          onClose={() => setReviewStep("none")}
        />
      )}

      {/* Two dialogs rather than one with a branch in it: the backend keeps a
          single state for an appointment that did not take place, so the cross
          on the strip opens the same cancellation the table and the carousel
          do. Only the tick has a dialog of its own. */}
      {today && marking === "completed" && (
        <CompleteAppointmentModal
          appointment={today}
          firstName={firstName}
          busy={answering}
          onClose={() => setMarking(null)}
          onConfirm={() => handleAnswerToday("")}
        />
      )}

      {today && marking === "cancelled" && (
        <CancelAppointmentModal
          appointment={today}
          firstName={firstName}
          busy={answering}
          onClose={() => setMarking(null)}
          onConfirm={handleAnswerToday}
        />
      )}

      {sharedLink && linkView !== "none" && (
        <PreconsultationSharedModal
          clientName={client.name}
          phone={client.phone}
          link={sharedLink}
          fresh={linkView === "fresh"}
          onClose={() => setLinkView("none")}
        />
      )}
    </div>
  );
};

export default ClientDetailPage;
